import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../atoms/Button';
import Card from '../../atoms/Card';
import { OrderOut } from '../../../types/api';
import { apiService } from '../../../services/api';
import { usePriceFormat } from '../../../hooks/usePriceFormat';
import './BartenderOrderWorkflow.scss';

const QUEUE_STATUSES = ['pending', 'paid', 'preparing', 'ready'];

interface BartenderOrderWorkflowProps {
  order: OrderOut;
  venueName?: string | null;
  onDelivered: (order: OrderOut) => void;
  // Called after "Reject" released the order, or on "Back" for an order that
  // is already delivered/cancelled
  onClose: () => void;
  className?: string;
}

/**
 * Step 2 of the bartender workflow: one order, large, with the only two
 * actions a bartender takes at the bar. "Delivered" completes it; "Reject"
 * puts it back in the queue untouched. Cancelling happens elsewhere, from the
 * order's detail in the order history, to avoid cancelling by mistake here.
 */
const BartenderOrderWorkflow: React.FC<BartenderOrderWorkflowProps> = ({
  order,
  venueName,
  onDelivered,
  onClose,
  className = ''
}) => {
  const { t } = useTranslation();
  const { formatPrice } = usePriceFormat();
  const [busyAction, setBusyAction] = useState<'deliver' | 'reject' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const status = order.status.toLowerCase();
  const isActive = QUEUE_STATUSES.includes(status);
  const isCashDue = status === 'pending';
  const customerName = order.customer
    ? [order.customer.first_name, order.customer.last_name].filter(Boolean).join(' ')
    : '';

  const run = async (action: 'deliver' | 'reject') => {
    setBusyAction(action);
    setError(null);
    try {
      if (action === 'deliver') {
        onDelivered(await apiService.deliverOrder(order.id));
      } else {
        await apiService.releaseOrder(order.id);
        onClose();
      }
    } catch (actionError: any) {
      console.error(`Failed to ${action} order:`, actionError);
      setError(actionError?.response?.data?.detail || t('bartender.orders.updateError'));
    } finally {
      setBusyAction(null);
    }
  };

  return (
    <Card className={`bartender-order-workflow ${className}`}>
      <div className="bartender-order-workflow__head">
        <span className="bartender-order-workflow__id">#{order.id}</span>
        {venueName && <span className="bartender-order-workflow__venue">{venueName}</span>}
      </div>

      {customerName && <div className="bartender-order-workflow__customer">{customerName}</div>}

      {!isActive && (
        <div className="bartender-order-workflow__notice">
          {t('bartender.workflow.notActive', {
            status: t(`orderConfirmation.statuses.${status}`)
          })}
        </div>
      )}

      {isActive && isCashDue && (
        <div className="bartender-order-workflow__cash">
          <span>{t('bartender.workflow.collectCash')}</span>
          <strong>{formatPrice(order.total_amount)}</strong>
        </div>
      )}

      <ul className="bartender-order-workflow__items">
        {order.items.map((item) => (
          <li key={item.id} className="bartender-order-workflow__item">
            <span className="bartender-order-workflow__qty">{item.quantity}×</span>
            <span className="bartender-order-workflow__name">{item.drink_name || item.food_name}</span>
          </li>
        ))}
      </ul>

      {error && <div className="bartender-order-workflow__error">{error}</div>}

      <div className="bartender-order-workflow__actions">
        {isActive ? (
          <>
            <Button
              variant="success"
              size="lg"
              onClick={() => run('deliver')}
              loading={busyAction === 'deliver'}
              disabled={busyAction !== null}
              className="bartender-order-workflow__btn"
            >
              {isCashDue ? t('bartender.workflow.collectAndDeliver') : t('bartender.workflow.deliver')}
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => run('reject')}
              loading={busyAction === 'reject'}
              disabled={busyAction !== null}
              className="bartender-order-workflow__btn"
            >
              {t('bartender.workflow.reject')}
            </Button>
          </>
        ) : (
          <Button variant="secondary" size="lg" onClick={onClose} className="bartender-order-workflow__btn">
            {t('bartender.workflow.back')}
          </Button>
        )}
      </div>
    </Card>
  );
};

export default BartenderOrderWorkflow;
